import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useDisputes } from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";
import type { DisputeStatus } from "@/lib/payments/types";
import { statusDispute } from "@/lib/status";

const TABS: Array<{ key: DisputeStatus | undefined; label: string }> = [
	{ key: undefined, label: "Tous" },
	{ key: "OPEN", label: "Ouverts" },
	{ key: "NEED_EVIDENCE", label: "Preuves" },
	{ key: "UNDER_REVIEW", label: "En revue" },
	{ key: "CLOSED", label: "Clôturés" },
];

export function AdminDisputesPage() {
	const navigate = useNavigate();
	const [tab, setTab] = useState<DisputeStatus | undefined>(undefined);
	const { data, isLoading } = useDisputes(tab);
	const items = data ?? [];

	return (
		<>
			<PageHead
				title="Litiges & fraude"
				sub="SLA prestataire 5 jours ouvrés · rappel J+3, escalade J+5"
				actions={
					<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
						<AdminIcon n="chevL" s={14} />
						Retour
					</AdminButton>
				}
			/>

			<Card>
				<div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
					<div
						className="filter-bar"
						style={{ marginBottom: 0, flexWrap: "wrap", gap: 6 }}
					>
						{TABS.map((t) => (
							<ChipFilter
								key={t.key ?? "all"}
								on={tab === t.key}
								onClick={() => setTab(t.key)}
							>
								{t.label}
							</ChipFilter>
						))}
					</div>
				</div>

				{isLoading ? (
					<CardBody>
						<div className="empty">Chargement…</div>
					</CardBody>
				) : items.length === 0 ? (
					<CardBody>
						<div className="empty">Aucun litige avec ce filtre.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Référence</th>
									<th>Type</th>
									<th>Paiement</th>
									<th className="right">Montant</th>
									<th>Statut</th>
									<th>Échéance</th>
									<th>Résolution</th>
								</tr>
							</thead>
							<tbody>
								{items.map((d) => {
									const s = statusDispute(d.status);
									const overdue = new Date(d.dueResponseAt) < new Date();
									return (
										<tr key={d.id}>
											<td className="mono">{d.reference}</td>
											<td>{d.kind}</td>
											<td className="mono muted">
												{d.payment?.reference ?? d.paymentId.slice(0, 8)}
											</td>
											<td className="right tn">
												<strong>{fmtFCFA(d.amountDisputed)}</strong>
											</td>
											<td>
												<Tag variant={s.cls}>{s.l}</Tag>
											</td>
											<td
												className="mono"
												style={
													overdue
														? {
																color: "var(--a-danger, #b23a2a)",
																fontWeight: 600,
															}
														: { color: "var(--a-ink-mute)" }
												}
											>
												{new Date(d.dueResponseAt).toLocaleDateString("fr-FR")}
												{overdue && " ⚠️"}
											</td>
											<td className="muted">{d.resolution ?? "—"}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	);
}
